<script setup lang="ts">
import MainLayout from '@/common/components/MainLayout.vue';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';
import TabPanels from 'primevue/tabpanels';
import TabPanel from 'primevue/tabpanel';
import { computed, ref, watch } from 'vue';
import { useLanguageStore } from 'shared/translations/stores/languageStore';
import { storeToRefs } from 'pinia';
import { useRoute, useRouter } from 'vue-router';
import PackagesListTab from './components/PackagesListTab.vue';
import PackagingListTab from './components/PackagingListTab.vue';
import PackageTypesListTab from './components/PackageTypesListTab.vue';

type PackagesTab = 'packages' | 'packaging' | 'packageTypes';

function normalizeTab(tab: unknown): PackagesTab {
  if (tab === 'packaging') return 'packaging';
  if (tab === 'packageTypes') return 'packageTypes';
  return 'packages';
}

const languageStore = useLanguageStore();
const { translations } = storeToRefs(languageStore);

const route = useRoute();
const router = useRouter();
const selectedTab = ref<PackagesTab>(normalizeTab(route.query.tab));

const tabs = computed(() => [
  { value: 'packages' as const, label: translations.value.sideMenu.packages },
  { value: 'packaging' as const, label: translations.value.sideMenu.packaging },
  { value: 'packageTypes' as const, label: translations.value.sideMenu.packageTypes },
]);

watch(
  () => route.query.tab,
  (queryTab) => {
    const normalized = normalizeTab(queryTab);
    if (selectedTab.value !== normalized) {
      selectedTab.value = normalized;
    }
  },
);

watch(selectedTab, async (tab) => {
  const current = normalizeTab(route.query.tab);
  if (current === tab) return;

  await router.replace({
    name: 'packages',
    query: {
      ...route.query,
      tab,
    },
  });
});
</script>

<template>
  <MainLayout>
    <Tabs v-model:value="selectedTab">
      <TabList class="packages-tab-list">
        <Tab v-for="tab in tabs" :key="tab.value" :value="tab.value">{{ tab.label }}</Tab>
      </TabList>
      <TabPanels class="packages-tab-panels">
        <TabPanel value="packages">
          <PackagesListTab v-if="selectedTab === 'packages'"></PackagesListTab>
        </TabPanel>
        <TabPanel value="packaging">
          <PackagingListTab v-if="selectedTab === 'packaging'"></PackagingListTab>
        </TabPanel>
        <TabPanel value="packageTypes">
          <PackageTypesListTab v-if="selectedTab === 'packageTypes'"></PackageTypesListTab>
        </TabPanel>
      </TabPanels>
    </Tabs>
  </MainLayout>
</template>

<style scoped>  
:deep(.packages-tab-list.p-tablist) {
  border-top-left-radius: 12px;
  border-top-right-radius: 12px;
  overflow: hidden;
}

:deep(.packages-tab-panels.p-tabpanels) {
  border-bottom-left-radius: 12px;
  border-bottom-right-radius: 12px;
  overflow: hidden;
}

</style>
